import { atom, selector } from "recoil";
import defaultConfiguration from "../../public/config.json";
import { ConfigurationService } from "../services/configurationService";
import {
  CalculateTrajactoryInput,
  ExtendedTrajectoryData,
  TrajectoryCalculationService,
} from "../services/trajectoryCalculationService";
import {
  Configuration,
  ImageWithPivot,
  MediaType,
  MonitorConfiguration,
  NozzleConfiguration,
  Obstacle,
  ObstacleInput,
  Units,
  WorkingAreaConfiguration,
} from "../types/sharedTypes";

export const configurationState = atom<Configuration>({
  key: "configurationState",
  default: defaultConfiguration,
});

export const unitsState = atom<Units>({
  key: "unitsState",
  default: Units.Metric,
});

export const maxNumberOfRoofObstaclesSelecter = selector<number>({
  key: "maxNumberOfRoofObstaclesSelecter",
  get: ({ get }) => {
    const configuration = get(configurationState);
    return configuration.global.maxNumberOfRoofObstacles;
  },
});

export const maxNumberOfGroundObstaclesSelecter = selector<number>({
  key: "maxNumberOfGroundObstaclesSelecter",
  get: ({ get }) => {
    const configuration = get(configurationState);
    return configuration.global.maxNumberOfGroundObstacles;
  },
});

export const selectableMonitorConfigurationsSelector = selector<
  Array<MonitorConfiguration>
>({
  key: "selectableMonitorConfigurationsSelector",
  get: ({ get }) => {
    const configuration = get(configurationState);
    return configuration.monitors ?? [];
  },
});

export const selectableNozzleConfigurationSelector = selector<
  Array<NozzleConfiguration>
>({
  key: "selectableNozzleConfigurationSelector",
  get: ({ get }) => {
    const configuration = get(configurationState);
    const selectedMonitor = get(selectedMonitorSelector);
    if (!selectedMonitor) {
      return [];
    }
    return (configuration.nozzles ?? []).filter((nozzle) =>
      selectedMonitor.compatibleNozzles
        ?.map((e) => e.id)
        .includes(nozzle.id ?? "")
    );
  },
});

export const selectedMonitorImageSelector = selector<ImageWithPivot | null>({
  key: "selectedMonitorImage",
  get: ({ get }) => {
    const selectedMonitor = get(selectedMonitorSelector);
    return selectedMonitor?.image ?? null;
  },
});

export const selectedMonitorNozzleImageSelector =
  selector<ImageWithPivot | null>({
    key: "selectedMonitorNozzleImage",
    get: ({ get }) => {
      const selectedMonitor = get(selectedMonitorSelector);
      const selectedNozzleId = get(selectedNozzleIdState);
      return (
        selectedMonitor?.compatibleNozzles?.find(
          (e) => e.id === selectedNozzleId
        )?.image ?? null
      );
    },
  });

export const selectedMonitorIdState = atom<string | null>({
  key: "selectedMonitorIdState",
  default: null,
});

export const selectedNozzleIdState = atom<string | null>({
  key: "selectedNozzleIdState",
  default: null,
});

export const selectedMonitorSelector = selector<MonitorConfiguration | null>({
  key: "selectedMonitorState",
  get: ({ get }) => {
    const selectableMonitors = get(selectableMonitorConfigurationsSelector);
    const selectedMonitorId = get(selectedMonitorIdState);
    return selectableMonitors.find((m) => m.id === selectedMonitorId) ?? null;
  },
});

export const selectedNozzleSelector = selector<NozzleConfiguration | null>({
  key: "selectedNozzleSelector",
  get: ({ get }) => {
    const selectableNozzles = get(selectableNozzleConfigurationSelector);
    const selectedNozzleId = get(selectedNozzleIdState);
    return selectableNozzles.find((m) => m.id === selectedNozzleId) ?? null;
  },
});

/**
 * Flow Rate
 */

export const flowRateBoundariesSelector =
  selector<WorkingAreaConfiguration | null>({
    key: "flowRateBoundaries",
    get: ({ get }) => {
      const selectedMonitor = get(selectedMonitorSelector);
      const selectedPressure = get(selectedPressureState);
      const workingAreaConfiguration =
        ConfigurationService.selectConfigurationValue({
          needle: selectedPressure ?? 0,
          sortedHaystack: [...(selectedMonitor?.workingArea ?? [])].sort(
            (a, b) => a.pressure - b.pressure
          ),
          accessor: (e) => e.pressure,
        });

      return workingAreaConfiguration;
    },
  });

export const flowRateStepSelector = selector<number>({
  key: "flowRateStepSelector",
  get: ({ get }) => {
    const selectedMonitor = get(selectedMonitorSelector);
    return selectedMonitor?.flowRateStep ?? 0;
  },
});

export const selectedFlowRateState = atom<number>({
  key: "selectedFlowRateState",
  default: 0,
});

/**
 * Pressure
 */

export const pressureMinValueSelector = selector<number>({
  key: "pressureMinValueSelector",
  get: ({ get }) => {
    const configuration = get(configurationState);
    return (
      configuration?.pressures?.reduce<number | null>((acc, current) => {
        if (!acc) {
          return current.pressure;
        }
        return Math.min(acc, current.pressure);
      }, null) ?? 0
    );
  },
});

export const pressureMaxValueSelector = selector<number>({
  key: "pressureMaxValueSelector",
  get: ({ get }) => {
    const configuration = get(configurationState);
    const unit = get(unitsState);
    return (
      configuration?.pressures?.reduce<number | null>((acc, current) => {
        if (!acc) {
          return current.pressure;
        }
        return Math.max(acc, current.pressure);
      }, null) ?? 0
    );
  },
});

export const pressureStepSelector = selector<number>({
  key: "pressureStepSelector",
  get: ({ get }) => {
    const configuration = get(configurationState);
    return configuration?.global.pressureStep;
  },
});

export const selectedPressureState = atom<number>({
  key: "selectedPressureState",
  default: 0,
});

/**
 * Monitor Height
 */

export const monitorHeightMinValueSelector = selector<number>({
  key: "monitorHeightMinValueSelector",
  get: ({ get }) => {
    const configuration = get(configurationState);
    return configuration?.global.monitorHeightMin ?? 0;
  },
});

export const monitorHeightMaxValueSelector = selector<number>({
  key: "monitorHeightMaxValueSelector",
  get: ({ get }) => {
    const configuration = get(configurationState);
    return configuration?.global.monitorHeightMax ?? 0;
  },
});

export const monitorHeightStepSelector = selector<number>({
  key: "monitorHeightStepSelector",
  get: ({ get }) => {
    const configuration = get(configurationState);
    return configuration?.global.monitorHeightStep ?? 0;
  },
});

export const selectedMonitorHeightState = atom<number>({
  key: "selectedMonitorHeightState",
  default: 0,
});

/**
 * Monitor Angle
 */

export const angleMinValueSelector = selector<number>({
  key: "angleMinValueSelector",
  get: ({ get }) => {
    const configuration = get(configurationState);
    return configuration?.global.angleMin ?? 0;
  },
});

export const angleMaxValueSelector = selector<number>({
  key: "angleMaxValueSelector",
  get: ({ get }) => {
    const configuration = get(configurationState);
    return configuration?.global.angleMax ?? 0;
  },
});

export const angleStepSelector = selector<number>({
  key: "angleStepSelector",
  get: ({ get }) => {
    const configuration = get(configurationState);
    return configuration?.global.angleStep ?? 0;
  },
});

export const selectedAngleState = atom<number>({
  key: "selectedAngleState",
  default: 30,
});

/**
 * Medium
 */
export const selectedMediumState = atom<MediaType>({
  key: "selectedMediumState",
  default: "water",
});

/**
 * Obstacles
 */
export const roofObstaclesState = atom<Array<ObstacleInput>>({
  key: "roofObstaclesState",
  default: [],
});

export const groundObstaclesState = atom<Array<ObstacleInput>>({
  key: "groundObstaclesState",
  default: [],
});

/**
 * Input Aggregate
 */

const groundObstacleInputToObstacle = (input: ObstacleInput): Obstacle => ({
  bottom: 0,
  top: input.height,
  left: input.distance,
  right: input.distance + input.depth,
});

const roofObstacleInputToObstacle = (input: ObstacleInput): Obstacle => ({
  top: null,
  bottom: input.height,
  left: input.distance,
  right: input.distance + input.depth,
});

export const calculateTrajactoryInputSelector =
  selector<CalculateTrajactoryInput>({
    key: "calculateTrajactoryInputSelector",
    get: ({ get }) => {
      const monitorId = get(selectedMonitorIdState) ?? "";
      const nozzleId = get(selectedNozzleIdState) ?? "";
      const flowRate = get(selectedFlowRateState);
      const pressure = get(selectedPressureState);
      const angle = get(selectedAngleState);
      const monitorHeight = get(selectedMonitorHeightState);
      const mediaType = get(selectedMediumState);
      const obstacles = [
        ...get(roofObstaclesState)
          .filter((e) => e.depth >= 1 && e.distance >= 1 && e.height >= 1)
          .map(roofObstacleInputToObstacle),

        ...get(groundObstaclesState)
          .filter((e) => e.depth >= 1 && e.distance >= 1 && e.height >= 1)
          .map(groundObstacleInputToObstacle),
      ];

      return {
        monitorId,
        nozzleId,
        flowRate,
        pressure,
        angle,
        monitorHeight,
        mediaType,
        obstacles,
      };
    },
  });

/**
 * Output
 */

export const trajectoryCalculationServiceSelector =
  selector<TrajectoryCalculationService>({
    key: "trajectoryCalculationServiceSelector",
    get: ({ get }) => {
      const configuration = get(configurationState);
      return new TrajectoryCalculationService(configuration);
    },
  });

export const outputSelector = selector<ExtendedTrajectoryData | null>({
  key: "outputSelector",
  get: ({ get }) => {
    const input = get(calculateTrajactoryInputSelector);
    const trajectoryCalculationService = get(
      trajectoryCalculationServiceSelector
    );
    return trajectoryCalculationService.calculateExtendedTrajectoryData(input);
  },
});

export const infoModalAtom = atom<string | undefined>({
  key: "infoModal",
  default: undefined,
});

export const printLocalAtom = atom<boolean>({
  key: "printLocal",
  default: false,
});
